<template>
  <div class="w-full text-gray-darker py-2">
    <div v-if="!hideHeader && notes.length && !hidePatient">
      {{ patientName }}
    </div>
    <div class="mb-5" v-if="!hideHeader && notes.length">
      {{ `${headerRoomName} ${treatmentFullName} ${treatmentNameComputed ? `(${treatmentNameComputed})` : ''}` }}
    </div>
    <template v-if="editedNotes.length">
      <v-text-field
        v-for="(note, i) in editedNotes"
        :ref="`${patientName}_note_input_${i}`"
        :key="`note.-.${note.uuid}.-.${i}`"
        class="mb-4"
        :hint="`${$dayjs(note.created_at).format('MMM D, YYYY hh:mm A')}`"
        :label="`Note of ${!!note.user ? note.user.name : 'Deleted user'}`"
        :append-inner-icon="!!note.user && currentUser === note.user.uuid && !note.new ? 'mdi-checkbox-marked-circle-outline' : undefined"
        :append-icon="!!note.user && currentUser === note.user.uuid && !note.new ? 'mdi-delete' : undefined"
        :readonly="!!note.user && currentUser !== note.user.uuid || note.new"
        persistent-hint
        v-model="editedNotes[i].message"
        variant="outlined"
        density="compact"
        @click:append-inner="editTreatmentNote(i)"
        @click:append="deleteTreatmentNote(i)"
        @keydown.enter="editTreatmentNote(i);"
        @click="handleFocusedInput(`note.-.${note.uuid}.-.${i}`, editedNotes[i]?.message)"
        @blur="checkIsSaved(i)"
      ></v-text-field>
    </template>
    <v-btn
      variant="plain"
      :disabled="adding"
      class="mb-4 pl-0"
      color="grey-lighten-500"
      @click="newNoteFocus"
    >
      Add Note {{treatmentNameComputed ? `in ${treatmentNameComputed}` : ''}} +
    </v-btn>
    <v-text-field
      v-if="adding"
      ref="new-note"
      append-inner-icon="mdi-checkbox-marked-circle-outline"
      append-icon="mdi-delete"
      key="note-treatmentDialogAdd"
      :class="{'mb-4': !hideHeader }"
      :color="error ? 'red' : undefined"
      :hint="hint"
      label="Add a note"
      v-model="newNote"
      variant="outlined"
      density="compact"
      :persistent-hint="error"
      @click:append-inner="createNote($event)"
      @click:append="toggleOffAddingNote"
      @click="handleFocusedInput(`note-treatmentDialogAdd`, newNote)"
      @blur="checkIsSavedNewNote"
    ></v-text-field>

    <v-dialog v-model="confirmDeleteOpen" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Delete note?</v-card-title>
        <v-card-text>Are you sure you want to delete this note? This cannot be undone.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelDeleteNote">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDeleteNote">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {mapState} from 'vuex';

export default defineComponent({
  name: 'TreatmentNotesCard',
  props: {
    notes: Array,
    treatmentId: {
      type: String,
      default: '',
    },
    apptTreatmentId: {
      type: String,
      default: '',
    },
    appointmentId: {
      type: String,
      default: '',
    },
    patientId: {
      type: String,
      default: '',
    },
    patientName: {
      type: String,
      default: '',
    },
    treatmentName: {
      type: String,
      default: '',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hidePatient: {
      type: Boolean,
      default: false,
    },
    isAppointmentDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      adding: false,
      newNote: '',
      error: false,
      hint: '',
      editedNotes: [],
      currentUser: '',
      confirmDeleteOpen: false,
      pendingDeleteIndex: null as number | null,
    };
  },
  computed: {
    ...mapState({
      // @ts-ignore
      virtualKeyboardValues: (state) => state.virtualKeyboardValues,
      // @ts-ignore
      virtualKeyboardRelatedId: (state) => state.virtualKeyboardRelatedId,
      // @ts-ignore
      showVirtualKeyboard: (state) => state.showVirtualKeyboard,
    }),
    headerRoomName() {
      const name = this.notes[0].appointment_treatment?.room?.name ?? '';
      return `${name} ${name ? ' | ' : ''}`;
    },
    treatmentFullName() {
      return this.notes[0].treatment?.full_name ?? 'Global';
    },
    treatmentNameComputed() {
      return this.treatmentName && this.treatmentName !== 'Global' ? this.treatmentName : (this.notes[0]?.treatment?.name ?? '');
    },
  },
  mounted() {
    this.copyModelFromProps();
  },
  watch: {
    notes() {
      this.copyModelFromProps();
    },
    virtualKeyboardValues() {
      const virtualKeyboardRelatedIdExploded = this.virtualKeyboardRelatedId.split('.-.');

      if (this.virtualKeyboardRelatedId === 'note-treatmentDialogAdd') {
        this.newNote = this.virtualKeyboardValues;
      } else if ( virtualKeyboardRelatedIdExploded[0] === 'note' ) {
        const key = parseInt(virtualKeyboardRelatedIdExploded[2]);
        this.editedNotes[key].message = this.virtualKeyboardValues;
      }
    },
    newNote() {
      if (this.newNote !== this.virtualKeyboardValues) {
        this.$store.dispatch('setKeyboardInput', { input: this.newNote });
      }
      if (this.newNote === '') {
        this.$emit('set-allowed-close', true);
      }
    },
  },
  created() {
    this.currentUser = this.$store.state.auth.user.uuid;
    this.copyModelFromProps();
  },
  methods: {
    defaultProps() {
      return {

      };
    },
    async createNote(event) {
      event.target.blur();
      const payload = {
        params: {
          message: this.newNote,
          appointment_treatment: this.treatmentNameComputed ? this.apptTreatmentId : null,
          treatment_id: this.treatmentNameComputed ? this.treatmentId : null,
          patient_id: this.patientId,
        },
      };

      const newNoteBefore = this.newNote;

      const optimisticNote = {
        uuid: `temp-${Date.now()}`,
        message: this.newNote,
        created_at: new Date().toISOString(),
        user: this.$store.state.auth.user,
        new: true,
      };
      this.editedNotes.push(optimisticNote);

      this.newNote = '';
      this.adding = false;

      const success = await this.$store.dispatch('CREATE_NOTE', payload);
      if (success) {
        this.$eventBus.emit('emit-success', { state: true, text: 'Added note.' });
        this.$emit('set-allowed-close', true);
        this.$emit('refresh-notes');

      } else {
        this.editedNotes = this.editedNotes.filter((n) => n.uuid !== optimisticNote.uuid);
        this.$eventBus.emit('emit-error', { state: true, text: 'Couldn\'t create a note, something went wrong!' });
        this.newNote = newNoteBefore;
        this.adding = true;
      }
    },
    handleFocusedInput(idOfRelated, message) {
      if (this.virtualKeyboardRelatedId !== idOfRelated && this.showVirtualKeyboard) {
        this.$store.dispatch('setVirtualKeyboardRelatedId', { idOfRelated });
        this.$store.dispatch('setKeyboardInput', { input: message });
      }
    },
    toggleOffAddingNote() {
      this.newNote = '';
      this.adding = false;
      this.$emit('set-allowed-close', true);
    },
    async editTreatmentNote(i) {
      this.editedNotes[i].message = this.editedNotes[i].message.trim();

      if (this.adding) {
        return false;
      }

      if (this.editedNotes[i].message !== this.notes[i].message) {
        const payload = {
          params: {
            uuid: this.editedNotes[i].uuid,
            message: this.editedNotes[i].message,
            appointment_treatment: this.apptTreatmentId || null,
          },
        };
        const success = await this.$store.dispatch('UPDATE_NOTE', payload);
        if (success) {
          this.$emit('set-allowed-close', true);
          this.$eventBus.emit('emit-success', {state: true, text: 'Note updated.'});
          await this.$emit('refresh-notes');
          this.copyModelFromProps();
        } else {
          this.$eventBus.emit('emit-error', {state: true, text: 'Note could not update.'});
          this.copyModelFromProps();
        }
      }

    },
    newNoteFocus() {
      this.adding = true;

      this.$nextTick(() => {
        this.$refs[`new-note`].focus();
      });

      this.handleFocusedInput('note-treatmentDialogAdd', this.newNote);
    },
    checkIsSaved(i) {
      if (!this.editedNotes[i]) return;
      this.editedNotes[i].message = this.editedNotes[i].message.trim();

      if (this.editedNotes[i].message !== this.notes[i]?.message) {
        this.$nextTick(() => {
          this.$refs[`${this.patientName}_note_input_${i}`][0].focus();
        });

        this.$emit('set-allowed-close', false);
        this.$eventBus.emit('emit-error', {state: true, text: 'Please, save the latest changes in the notes so as not to lose the update!'});
      } else {
        this.$emit('set-allowed-close', true);
      }
    },
    checkIsSavedNewNote(): boolean {
      const notesIsNull = ['', null, undefined].includes(this.newNote);
      if (!notesIsNull) {
        this.$nextTick(() => {
          this.$refs[`new-note`].focus();
        });

        this.$emit('set-allowed-close', false);
        this.$eventBus.emit('emit-error', {state: true, text: 'Please, save or delete your new note!'});

        return false;
      } else {
        this.$emit('set-allowed-close', true);
      }

      return true;
    },
    deleteTreatmentNote(i) {
      if (this.adding) { return; }
      this.pendingDeleteIndex = i;
      this.confirmDeleteOpen = true;
    },
    cancelDeleteNote() {
      this.confirmDeleteOpen = false;
      this.pendingDeleteIndex = null;
    },
    async confirmDeleteNote() {
      const i = this.pendingDeleteIndex;
      this.confirmDeleteOpen = false;
      this.pendingDeleteIndex = null;
      if (i === null || !this.editedNotes[i]) { return; }

      const payload = {
        params: {
          uuid: this.editedNotes[i].uuid,
          appointment_treatment: this.apptTreatmentId || this.editedNotes[i].appointment_treatment?.uuid,
        },
      };
      const success = await this.$store.dispatch('DELETE_NOTE', payload);
      if (success) {
        this.$eventBus.emit('emit-success', {state: true, text: 'Note deleted.'});
        await this.$emit('refresh-notes');
      } else {
        this.$eventBus.emit('emit-error', {state: true, text: 'Note could not DELETE.'});
      }
    },
    showError() {
        this.error = true;
        this.hint = 'Something went wrong. Please try again.';
        setTimeout(() => {
          this.error = false;
          this.hint = '';
        }, 3000);
    },
    copyModelFromProps() {
      this.editedNotes = JSON.parse(JSON.stringify(this.notes));
    },
  },
});
</script>

<style scoped>
:deep(.v-field__input) {
  padding-left: 12px;
}

:deep(.v-field__append-inner) {
  align-items: center;
  padding-top: 0;
}

:deep(.v-input__append) {
  align-items: flex-start;
  padding-top: 8px;
  margin-left: 8px;
}
</style>
